import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DndContext, closestCenter, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, ChevronLeft, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { Input, Textarea, Label, Checkbox, Button } from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { GET_ONBOARDING_STEP_REGISTRY } from '../../../graphql/queries';
import { GET_ONBOARDING_VERSION } from './stepMappers';
import { CREATE_ONBOARDING_VERSION } from '../../../graphql/mutations';
import { StepConfigForm } from './StepConfigForm';
import { useRegistryByCode, mapVersionStepsToEditorSteps } from './stepMappers';
import { PreviewModal } from './PreviewModal';
import type { EditorStep, RegistryEntry } from './types';

function StepDragPreview({ step }: { step: EditorStep }) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-lg ring-1 ring-black/5">
      <div className="flex items-center gap-3">
        <GripVertical className="w-5 h-5 text-gray-400" />
        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-900 rounded">{step.stepCode}</span>
        <span className="text-sm text-gray-500">Position: {step.position}</span>
      </div>
    </div>
  );
}

function SortableStepItem({
  step,
  registryByCode,
  collapsed,
  onToggleCollapsed,
  onUpdate,
  onRemove,
  onPreview,
}: {
  step: EditorStep;
  registryByCode: Map<string, RegistryEntry>;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onUpdate: (updates: Partial<EditorStep>) => void;
  onRemove: () => void;
  onPreview: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const reg = registryByCode.get(step.stepCode);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-gray-200 rounded-lg p-4 mb-2 ${step.isEnabled ? '' : 'opacity-60'}`}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="flex shrink-0 cursor-grab touch-none items-center justify-center rounded active:cursor-grabbing"
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand step' : 'Collapse step'}
            className="shrink-0 text-gray-500"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-900 rounded">{step.stepCode}</span>
              <span className="text-sm text-gray-500">Position: {step.position}</span>
              <Label htmlFor={`enabled-${step.id}`} className="flex items-center gap-2 text-sm font-normal">
                <Checkbox
                  id={`enabled-${step.id}`}
                  checked={step.isEnabled}
                  onCheckedChange={(checked) => onUpdate({ isEnabled: checked === true })}
                />
                Enabled
              </Label>
              {!reg?.isPostCompletion && (
                <Label htmlFor={`skippable-${step.id}`} className="flex items-center gap-2 text-sm font-normal">
                  <Checkbox
                    id={`skippable-${step.id}`}
                    checked={!step.required}
                    disabled={!step.isEnabled}
                    onCheckedChange={(checked) => {
                      const skippable = checked === true;
                      onUpdate({
                        required: !skippable,
                        requiredOverride: skippable ? false : true,
                      });
                    }}
                  />
                  Skippable
                </Label>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onPreview}
                aria-label="Preview step"
                title="Preview this step"
                className="text-gray-400 hover:text-gray-700"
              >
                <Eye className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onRemove}
                aria-label="Remove step from version"
                className="text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        {!collapsed && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`description-${step.id}`} className="block mb-1">
                  Description
                </Label>
                <Textarea
                  id={`description-${step.id}`}
                  value={step.description}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  className="w-full text-sm"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor={`rationale-${step.id}`} className="block mb-1">
                  Rationale
                </Label>
                <Textarea
                  id={`rationale-${step.id}`}
                  value={step.rationale}
                  onChange={(e) => onUpdate({ rationale: e.target.value })}
                  className="w-full text-sm"
                  rows={2}
                />
              </div>
            </div>
            {reg ? (
              <StepConfigForm
                stepCode={step.stepCode}
                schema={reg.configSchema}
                value={step.configOverrides}
                onChange={(next) => onUpdate({ configOverrides: next })}
              />
            ) : (
              <p className="text-sm text-amber-600">Unknown step in registry: {step.stepCode}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function OnboardingEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<EditorStep[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInitialStep, setPreviewInitialStep] = useState(0);

  const { data: registryData } = useQuery<{ onboardingStepRegistry: RegistryEntry[] }>(GET_ONBOARDING_STEP_REGISTRY);
  const { data: versionData } = useQuery(GET_ONBOARDING_VERSION, {
    variables: { id },
    skip: !id,
  });

  const [createVersion, { loading: creating }] = useMutation(CREATE_ONBOARDING_VERSION);

  const registryByCode = useRegistryByCode(registryData?.onboardingStepRegistry);

  // Seed once per route context. The queries can hand back new result objects
  // after the first render (cache writes elsewhere re-broadcast them), and an
  // unguarded effect would then reseed the list and silently wipe unsaved
  // admin edits (observed live: Enabled toggles reverting minutes later).
  const seededRef = useRef<string | null>(null);
  const seedKey = id ?? 'new';

  useEffect(() => {
    if (seededRef.current === seedKey) return;
    if (versionData?.onboardingVersion) {
      seededRef.current = seedKey;
      const version = versionData.onboardingVersion;
      setName(`${version.name} (Copy)`);
      setDescription(version.description || '');
      const mapped = mapVersionStepsToEditorSteps(version.steps);
      setSteps(mapped);
      const nextCollapsed: Record<string, boolean> = {};
      for (const s of mapped) {
        nextCollapsed[s.id] = true;
      }
      setCollapsed(nextCollapsed);
    } else if (!id && registryData?.onboardingStepRegistry) {
      seededRef.current = seedKey;
      // Completion steps (and finales, which they can't mix with) start in the palette,
      // not the seed — seeding every registry step would produce an unsaveable version.
      // Same for UPLOAD_ARTWORK_REVIEW: it replaces UPLOAD_ARTWORK, never sits beside it.
      const seedable = registryData.onboardingStepRegistry.filter(
        (entry) =>
          !entry.isPostCompletion && !entry.code.startsWith('FINALE_') && entry.code !== 'UPLOAD_ARTWORK_REVIEW'
      );
      const mapped = seedable.map((entry, index) => ({
        id: entry.code,
        stepCode: entry.code,
        position: index + 1,
        isEnabled: true,
        required: entry.isRequired,
        requiredOverride: null,
        configOverrides: {},
        description: '',
        rationale: '',
      }));
      setSteps(mapped);
      const nextCollapsed: Record<string, boolean> = {};
      for (const s of mapped) {
        nextCollapsed[s.id] = true;
      }
      setCollapsed(nextCollapsed);
    }
  }, [versionData, registryData, id, seedKey]);

  const availableToAdd = useMemo(() => {
    if (!registryData?.onboardingStepRegistry) return [];
    const included = new Set(steps.map((s) => s.stepCode));
    return registryData.onboardingStepRegistry.filter((entry) => !included.has(entry.code));
  }, [registryData, steps]);

  const enabledSteps = useMemo(() => steps.filter((s) => s.isEnabled), [steps]);

  const activeDragStep = useMemo(
    () => (activeId ? (steps.find((s) => s.id === activeId) ?? null) : null),
    [activeId, steps]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({ ...item, position: index + 1 }));
      });
    }
  };

  const handleUpdateStep = (id: string, updates: Partial<EditorStep>) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  const handleRemoveStep = (id: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== id).map((s, idx) => ({ ...s, position: idx + 1 })));
  };

  const handleAddStep = (entry: RegistryEntry) => {
    setSteps((prev) => [
      ...prev,
      {
        id: entry.code,
        stepCode: entry.code,
        position: prev.length + 1,
        isEnabled: true,
        required: entry.isRequired,
        requiredOverride: null,
        configOverrides: {},
        description: '',
        rationale: '',
      },
    ]);
    setCollapsed((c) => ({ ...c, [entry.code]: false }));
  };

  const expandAllSteps = () => {
    setCollapsed((c) => {
      const next = { ...c };
      for (const s of steps) {
        next[s.id] = false;
      }
      return next;
    });
  };

  const collapseAllSteps = () => {
    setCollapsed((c) => {
      const next = { ...c };
      for (const s of steps) {
        next[s.id] = true;
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      const stepsInput = steps.map((s) => ({
        stepCode: s.stepCode,
        position: s.position,
        isEnabled: s.isEnabled,
        configOverrides: Object.keys(s.configOverrides).length > 0 ? s.configOverrides : null,
        requiredOverride: s.requiredOverride,
        description: s.description || null,
        rationale: s.rationale || null,
      }));

      const result = await createVersion({
        variables: {
          input: {
            name,
            description: description || null,
            steps: stepsInput,
          },
        },
      });

      if (result.data?.createOnboardingVersion) {
        navigate(`/admin/onboarding/${result.data.createOnboardingVersion.id}`);
      }
    } catch (error) {
      console.error('Error creating version:', error);
      alert('Error creating version: ' + (error as Error).message);
    }
  };

  return (
    <AdminLayout title="Onboarding Editor" description="Create or edit an onboarding flow version">
      <div className="space-y-6">
        <Link
          to="/admin/onboarding"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to versions
        </Link>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Version Info</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="version-name" className="block mb-1">
                Name
              </Label>
              <Input
                id="version-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full"
                placeholder="Version name"
              />
            </div>
            <div>
              <Label htmlFor="version-description" className="block mb-1">
                Description
              </Label>
              <Textarea
                id="version-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full"
                rows={2}
                placeholder="Optional description"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold">Steps (drag to reorder)</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {steps.length} {steps.length === 1 ? 'step' : 'steps'} in this version
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {steps.length > 0 && (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={expandAllSteps}>
                    Expand all
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={collapseAllSteps}>
                    Collapse all
                  </Button>
                  {enabledSteps.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPreviewInitialStep(-1);
                        setPreviewOpen(true);
                      }}
                      className="gap-1"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview
                    </Button>
                  )}
                </>
              )}
              <Button onClick={handleSave} disabled={creating || !name || steps.length === 0}>
                {creating ? 'Saving…' : 'Save as new version'}
              </Button>
            </div>
          </div>

          {steps.length === 0 ? (
            <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg p-6 text-center">
              No steps in this version yet. Add some from the picker below.
            </div>
          ) : (
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={(e) => setActiveId(String(e.active.id))}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setActiveId(null)}
            >
              <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {steps.map((step, idx) => (
                  <div key={step.id}>
                    {registryByCode.get(step.stepCode)?.isPostCompletion &&
                      !registryByCode.get(steps[idx - 1]?.stepCode ?? '')?.isPostCompletion && (
                        <div className="flex items-center gap-3 my-3 text-xs font-medium text-green-700">
                          <span className="h-px flex-1 bg-green-200" />
                          onboarding completes here — conversion fires, steps below are the bonus lap
                          <span className="h-px flex-1 bg-green-200" />
                        </div>
                      )}
                    <SortableStepItem
                      step={step}
                      registryByCode={registryByCode}
                      collapsed={collapsed[step.id] ?? true}
                      onToggleCollapsed={() => setCollapsed((c) => ({ ...c, [step.id]: !(c[step.id] ?? true) }))}
                      onUpdate={(updates) => handleUpdateStep(step.id, updates)}
                      onRemove={() => handleRemoveStep(step.id)}
                      onPreview={() => {
                        const enabledIdx = enabledSteps.findIndex((s) => s.id === step.id);
                        if (enabledIdx < 0) return;
                        setPreviewInitialStep(enabledIdx);
                        setPreviewOpen(true);
                      }}
                    />
                  </div>
                ))}
              </SortableContext>
              <DragOverlay>{activeDragStep ? <StepDragPreview step={activeDragStep} /> : null}</DragOverlay>
            </DndContext>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-1">Available steps</h3>
          <p className="text-sm text-gray-500 mb-4">
            Click any step to add it to the bottom of the list. Defaults come from the registry; you can override
            description, rationale, step options, and skippability per step.
          </p>
          {availableToAdd.length === 0 ? (
            <p className="text-sm text-gray-500">All registered steps are already in this version.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableToAdd.map((entry) => (
                <Button
                  key={entry.code}
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddStep(entry)}
                  className="font-normal"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {entry.code}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
      {previewOpen && enabledSteps.length > 0 && (
        <PreviewModal
          steps={enabledSteps}
          registryByCode={registryByCode}
          initialStepIndex={previewInitialStep}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </AdminLayout>
  );
}

export default OnboardingEditorPage;
